import { Database } from '@habity/types';

type User = Database['public']['Tables']['users']['Row'];

/**
 * Partial user type for profile completeness checks
 * Only requires the fields needed to determine profile completeness
 */
type PartialUserForProfileCheck = {
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  terms_accepted_at?: string | null;
  privacy_policy_accepted_at?: string | null;
};

/**
 * Checks if a user has a complete profile
 * A complete profile requires: first_name, last_name, phone, terms_accepted_at, and privacy_policy_accepted_at
 * 
 * This logic matches the backend /users/lookup endpoint
 * 
 * @param user - The user object to check (can be partial)
 * @returns true if the user has a complete profile, false otherwise
 */
export const hasCompleteProfile = (user: User | PartialUserForProfileCheck | null | undefined): boolean => {
  if (!user) return false;
  
  return !!(
    user.first_name?.trim() && 
    user.last_name?.trim() && 
    user.phone?.trim() && 
    user.terms_accepted_at &&
    user.privacy_policy_accepted_at
  );
};

/**
 * Gets the missing required fields for a user profile
 * 
 * @param user - The user object to check (can be partial)
 * @returns Array of missing field names
 */
export const getMissingProfileFields = (user: User | PartialUserForProfileCheck | null | undefined): string[] => {
  if (!user) return ['first_name', 'last_name', 'phone', 'terms_accepted_at', 'privacy_policy_accepted_at'];
  
  const missing: string[] = [];
  
  if (!user.first_name?.trim()) missing.push('first_name');
  if (!user.last_name?.trim()) missing.push('last_name');
  if (!user.phone?.trim()) missing.push('phone');
  if (!user.terms_accepted_at) missing.push('terms_accepted_at');
  if (!user.privacy_policy_accepted_at) missing.push('privacy_policy_accepted_at');
  
  return missing;
};
